import type { PageContent } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import { fillTemplate } from '@accenturecodefoundry/mfe-pot-shared-content-client';
import type { GuidedParam, GuidedText } from 'employment-insurance-data-access';
import { formatCurrency, formatIsoDate } from '../format-date';
import { GUIDED_REPORTING_KEY_PREFIX } from './content-keys';

/**
 * Resolves the BFF's copy references (`GuidedText`) to display strings:
 * the key's CMS/fallback text, with each parameter formatted in the active
 * locale -- dates and amounts are never formatted on the server. Rendering
 * only; nothing here decides anything.
 */
export type Locale = 'en' | 'fr';

export type GuidedTextResolver = (text: GuidedText) => string;

const intlLocale = (locale: Locale) => (locale === 'fr' ? 'fr-CA' : 'en-CA');

/** A `YYYY-MM-DD` date as a short UTC date, e.g. "Apr 7" / "7 avr.". */
function shortDate(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(
    new Date(`${iso}T00:00:00Z`),
  );
}

const HH_MM = /^([01]\d|2[0-3]):([0-5]\d)$/;

/**
 * A stored `HH:MM` in the active locale: English stays 24-hour ("08:00",
 * matching the hour and minute selects), French is « 8 h 30 », and « 8 h »
 * on the hour -- Intl gives « 8 h 00 », so a zero minute part is dropped.
 */
export function formatTime(value: string, locale: Locale): string {
  const match = HH_MM.exec(value);
  if (!match) {
    return value;
  }
  const date = new Date(Date.UTC(2000, 0, 1, Number(match[1]), Number(match[2])));
  if (locale === 'en') {
    return new Intl.DateTimeFormat('en-CA', { hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'UTC' }).format(date);
  }
  const parts = new Intl.DateTimeFormat('fr-CA', { hour: 'numeric', minute: '2-digit', timeZone: 'UTC' }).formatToParts(date);
  const shown = match[2] === '00' ? parts.filter((part) => part.type !== 'minute') : parts;
  return shown
    .map((part) => part.value)
    .join('')
    .trim();
}

/**
 * A phone number in the active locale's convention: (416) 555-0123 in
 * English, 416 555-0123 in French -- from the stored 10 digits, or from a
 * value saved before phones were stored as digits. Anything that isn't a
 * 10-digit number (after a leading country code 1) renders as stored.
 */
export function formatPhone(value: string, locale: Locale): string {
  let digits = value.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    digits = digits.slice(1);
  }
  if (digits.length !== 10) {
    return value;
  }
  const [area, exchange, line] = [digits.slice(0, 3), digits.slice(3, 6), digits.slice(6)];
  return locale === 'fr' ? `${area} ${exchange}-${line}` : `(${area}) ${exchange}-${line}`;
}

function longRange(from: string, to: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  }).formatRange(new Date(`${from}T00:00:00Z`), new Date(`${to}T00:00:00Z`));
}

function joinWith(parts: string[], join: 'comma' | 'and' | 'semicolon' | 'dot', locale: Locale): string {
  switch (join) {
    case 'semicolon':
      return parts.join('; ');
    case 'dot':
      return parts.join(' · ');
    case 'and':
      // "A and B", "A, B and C": the prototype's list wording, no serial comma.
      return new Intl.ListFormat(intlLocale(locale), { style: 'long', type: 'conjunction' })
        .format(parts)
        .replace(/, (and|et) /, ' $1 ');
    default:
      return parts.join(', ');
  }
}

export function createResolver(content: Record<string, PageContent>, locale: Locale): GuidedTextResolver {
  const resolve: GuidedTextResolver = (text) => {
    const key = `${GUIDED_REPORTING_KEY_PREFIX}${text.key}`;
    const template = content[key]?.title ?? key;
    if (!text.params) {
      return template;
    }
    const values: Record<string, string> = {};
    for (const [name, param] of Object.entries(text.params)) {
      values[name] = formatParam(param);
    }
    return fillTemplate(template, values);
  };

  function formatParam(param: GuidedParam): string {
    switch (param.t) {
      case 'text':
        return param.v;
      case 'number':
        return new Intl.NumberFormat(intlLocale(locale)).format(param.v);
      case 'money':
        return formatCurrency(param.v, locale);
      case 'date':
        return formatIsoDate(param.v, locale) ?? param.v;
      case 'shortDate':
        return shortDate(param.v, locale);
      case 'time':
        return formatTime(param.v, locale);
      case 'phone':
        return formatPhone(param.v, locale);
      case 'daySpan':
        if (param.v.length === 0) return '';
        return param.v.length === 1
          ? shortDate(param.v[0], locale)
          : `${shortDate(param.v[0], locale)}–${shortDate(param.v[param.v.length - 1], locale)}`;
      case 'dayList':
        return param.v.map((day) => shortDate(day, locale)).join(', ');
      case 'range':
        return longRange(param.from, param.to, locale);
      case 'texts':
        return joinWith(param.v.map(resolve), param.join, locale);
      default: {
        // A param type this bundle doesn't know (a newer BFF than this MFE):
        // show its plain value rather than "undefined" inside the sentence.
        // Bundles built before this case still render "undefined", so it does
        // not replace shipping the MFE first (see GuidedParam).
        const unknown = param as unknown as { v?: unknown };
        return typeof unknown.v === 'string' || typeof unknown.v === 'number' ? String(unknown.v) : '';
      }
    }
  }

  return resolve;
}

/** "Mon Apr 7" / "lun. 7 avr." for a weekday chip. */
export function weekdayLabel(iso: string, locale: Locale): string {
  return new Intl.DateTimeFormat(intlLocale(locale), {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
  }).format(new Date(`${iso}T00:00:00Z`));
}
